import {
  SkyButton,
  SkyCodeBlock,
  SkyDialog,
  SkyMessageBar,
  SkySkeleton,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type {
  TransformationGenericTest,
  TransformationNodeSource,
  TransformationNodeSourceText,
} from "@shared/types/transformations";
import { useTransformationNodeSource } from "@/hooks/use-dbt-jobs";
import { usePermissions } from "@/hooks/use-permissions";
import { isNotFoundError } from "@/components/access/RecordNotFound";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { DONE_LABEL_MS } from "@/lib/feedback/NoticeRegion";
import { CopyIcon } from "@/lib/icons";
import { highlightCode } from "@/lib/transformations/sqlHighlight";
import { normaliseSql } from "@/lib/transformations/sqlText";

/**
 * "Query for {node}": the SOURCE SQL of one dbt model or test, as it is in the customer's
 * repository (dbt's `raw_code`, read from the run's source bundle).
 *
 * Owner decision 2026-10-14 (#1002, `docs/adr/0011-amendment-2026-10-14-no-compiled-sql.md`):
 * compiled and executed SQL is never shown. Compiled SQL is what dbt produces after its macros run,
 * and a macro can read the warehouse while it compiles, so it can carry row values skyData must
 * never show. The dialog says so in one line. There is no Compiled or Executed switch any more.
 * The source stays Tenant admins' and above (owner 2026-10-14, `instance.binding.identity.read`),
 * since the project's own code can name the loader identity: anyone else reads one plain line,
 * the same sentence the route answers its 403 with (the owner's words on #1002), and the route is
 * never called. The History and Tests views offer them no Query control at all.
 *
 * The read is AUDITED on the server and made once per open (the parent mounts this dialog only
 * while it is open; `useTransformationNodeSource` never refetches and drops the text when the
 * dialog closes). Nothing of it is logged, stored or put in a notice.
 *
 * The SQL sits in skypoint-ui's `SkyCodeBlock`, highlighted by the app's one highlighter
 * (`lib/transformations/sqlHighlight.tsx`); its code area scrolls and is focusable so a keyboard
 * can scroll it. The block's own copy button is off (`showCopy={false}`): the dialog's "Copy"
 * stays, because it confirms INSIDE the dialog: the button reads "Copied" for the done-label
 * interval and a polite status line says "Query copied.". Copy copies the plain text, no markup.
 * Never the page notice, which sits under the modal and is aria-hidden while it is open (ux-s10
 * §3 rule 2), and never a toast. A refused clipboard is said inside the dialog too.
 *
 * The text is shown through `normaliseSql` (owner feedback 2026-09-28): leading blank lines are
 * dropped, runs of blank lines become one, and the common indent goes. Copy copies exactly what
 * is shown. A text with nothing but whitespace says so, with no empty block and nothing to copy.
 */

export interface TransformationSqlTarget {
  runId: string;
  nodeId: string;
  /** The node's short name, for the title. */
  name: string;
  /** "message": opened from "View message", so focus goes to dbt's message line. */
  focus?: "sql" | "message";
  /**
   * The control that opened the dialog, from the click event (review nit: Safari on macOS does
   * not focus a button on click, so `document.activeElement` is not it there).
   */
  opener?: HTMLElement | null;
}

/**
 * The one line that says what the dialog shows and why compiled SQL is not shown (ADR 0011
 * amendment 2026-10-14), shown only beside the source block (review on #1027: it is false where
 * no SQL is shown). The text is that run's copy, read from the manifest it parsed, so the line
 * says "was ... for this run", never that it is the file as it is now. Customer copy: no dash and
 * no semicolon.
 */
export const SOURCE_SQL_ONLY =
  "This is the SQL as it was in your repository for this run. Compiled SQL is not shown, because it can hold values dbt read from your warehouse.";
const TRUNCATED = "This query is longer than 128 KB. Only the first 128 KB are shown.";
const NOT_RECORDED =
  "The source SQL of this node was not recorded. Runs before this release did not record it.";
/** A unit test only (its id starts `unit_test.`): it is written in YAML. */
export const UNIT_TEST_NO_SOURCE =
  "This node has no SQL file of its own. A unit test is written in YAML.";
/** Any other node the bundle lists with no source (review on #1027: never the unit test line). */
const NODE_NOT_RECORDED = "The source SQL of this node was not recorded.";
const OMITTED = "This run's queries passed the 4 MB limit, so this one was not kept.";
const EMPTY = "The SQL file of this node is empty.";
/**
 * Owner 2026-10-14 (#1002, the owner's own sentence): the source SQL is shown to Tenant admins
 * and above only (`instance.binding.identity.read`). Anyone else reads this line and no request
 * is made.
 */
export const SOURCE_SQL_TENANT_ADMIN_ONLY = "Model SQL is visible to Tenant admins.";

/**
 * A generic data test's line (review on #1027): its `raw_code` is dbt's generated call, not SQL
 * from the repository, so the dialog names the test, its column and its YAML file instead.
 */
export function genericTestLine(generic: TransformationGenericTest, path: string | null): string {
  const what = generic.column
    ? `This is dbt's ${generic.test} test on the column ${generic.column}.`
    : `This is dbt's ${generic.test} test.`;
  const where = path ? `It is defined in ${path}` : "It is defined in a YAML file of your project";
  return `${what} ${where}, so it has no SQL file of its own.`;
}

/** A read that failed for a reason other than "not recorded", with Try again. */
function SqlReadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title="We could not read this query"
      testId="transformation-sql-error"
      message={error instanceof Error ? error.message : "Something went wrong."}
      actions={
        <SkyButton
          appearance="secondary"
          size="small"
          testId="transformation-sql-retry"
          onClick={onRetry}
        >
          Try again
        </SkyButton>
      }
    />
  );
}

/**
 * The source as shown with the line about compiled SQL beside it, an empty file, or why the node
 * has none: a generic test's names, a unit test's YAML line, or "not recorded" (nothing when it
 * was omitted).
 */
function SqlText({
  data,
  shown,
  name,
}: {
  data: TransformationNodeSource;
  shown: string | null;
  name: string;
}) {
  const text: TransformationNodeSourceText | null = data.source;
  if (text && !shown) {
    return (
      <SkyText size={200} color="muted" block testId="transformation-sql-empty">
        {EMPTY}
      </SkyText>
    );
  }
  if (text) {
    return (
      <>
        <SkyText size={200} color="muted" block testId="transformation-sql-source-only">
          {SOURCE_SQL_ONLY}
        </SkyText>
        <SkyCodeBlock
          code={shown ?? ""}
          language="sql"
          title="Source SQL"
          highlight={highlightCode}
          showCopy={false}
          maxHeight="24rem"
          ariaLabel={`Source SQL of ${name}`}
          testId="transformation-sql-text"
        />
      </>
    );
  }
  if (data.omitted) return null;
  if (data.generic) {
    return (
      <SkyText size={200} color="muted" block testId="transformation-sql-generic">
        {genericTestLine(data.generic, data.path)}
      </SkyText>
    );
  }
  const unitTest = data.nodeId.startsWith("unit_test.");
  return (
    <SkyText size={200} color="muted" block testId="transformation-sql-none">
      {unitTest ? UNIT_TEST_NO_SOURCE : NODE_NOT_RECORDED}
    </SkyText>
  );
}

/** dbt's first message line, focusable so "View message" can land on it. */
function DbtMessage({
  message,
  messageRef,
}: {
  message: string;
  messageRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={messageRef}
      tabIndex={-1}
      className="rounded-md border border-border bg-muted px-3 py-2"
      data-testid="transformation-sql-message"
    >
      <SkyText size={200}>
        {"dbt's message (first line, values masked): "}
        <span className="font-mono">{message}</span>
      </SkyText>
    </div>
  );
}

/** The file's path, the Copy button, and the line that announces the copy. */
function SqlToolbar({
  path,
  copied,
  canCopy,
  onCopy,
}: {
  path: string | null;
  copied: boolean;
  canCopy: boolean;
  onCopy: () => void;
}) {
  return (
    <>
      <div
        className="flex min-w-0 flex-wrap items-center gap-2"
        data-testid="transformation-sql-toolbar"
      >
        {path ? (
          <SkyText size={200} color="muted" testId="transformation-sql-path">
            {"File: "}
            <span className="font-mono">{path}</span>
          </SkyText>
        ) : null}
        <SkyButton
          className="ml-auto"
          appearance="subtle"
          size="small"
          icon={CopyIcon}
          disabled={!canCopy}
          onClick={onCopy}
          testId="transformation-sql-copy"
        >
          {copied ? "Copied" : "Copy"}
        </SkyButton>
      </div>
      {/* Inside the modal, so it is not aria-hidden: the one line that announces the copy. */}
      <span role="status" className="sr-only" data-testid="transformation-sql-copy-status">
        {copied ? "Query copied." : ""}
      </span>
    </>
  );
}

/** What the read answered: dbt's message, the omitted note, the path and Copy, and the text. */
function SqlBody({
  data,
  shown,
  name,
  copied,
  messageRef,
  onCopy,
}: {
  data: TransformationNodeSource;
  shown: string | null;
  name: string;
  copied: boolean;
  messageRef: RefObject<HTMLDivElement | null>;
  onCopy: () => void;
}) {
  return (
    <>
      {data.message ? <DbtMessage message={data.message} messageRef={messageRef} /> : null}

      {data.omitted ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="transformation-sql-omitted"
          message={OMITTED}
        />
      ) : null}

      <SqlToolbar path={data.path} copied={copied} canCopy={Boolean(shown)} onCopy={onCopy} />

      {data.source?.truncated ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="transformation-sql-truncated"
          message={TRUNCATED}
        />
      ) : null}

      <SqlText data={data} shown={shown} name={name} />
    </>
  );
}

export function TransformationSqlDialog({
  target,
  onClose,
}: {
  target: TransformationSqlTarget;
  onClose: () => void;
}) {
  const copyError = useInlineError();
  const [copied, setCopied] = useState(false);
  const { can } = usePermissions();
  const mayRead = can("instance.binding.identity.read");
  const sql = useTransformationNodeSource(
    mayRead ? { runId: target.runId, nodeId: target.nodeId } : null,
  );
  const messageRef = useRef<HTMLDivElement>(null);
  // The control that opened the dialog, read during the first render (before the dialog takes
  // focus). The parent unmounts the dialog on close, so focus is handed back here explicitly.
  const [opener] = useState<HTMLElement | null>(() => {
    if (target.opener) return target.opener;
    return document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null;
  });
  useEffect(
    () => () => {
      if (opener?.isConnected) opener.focus();
    },
    [opener],
  );

  const data = sql.data;
  const text = data?.source ?? null;
  const notFound = isNotFoundError(sql.error);
  const shown = useMemo(() => (text ? normaliseSql(text.sql) : null), [text]);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), DONE_LABEL_MS);
    return () => clearTimeout(handle);
  }, [copied]);

  useEffect(() => {
    if (target.focus === "message" && data?.message) messageRef.current?.focus();
  }, [target.focus, data?.message]);

  const copy = async (): Promise<void> => {
    if (!shown) return;
    copyError.clear();
    setCopied(false);
    try {
      await navigator.clipboard.writeText(shown);
      setCopied(true);
    } catch (error) {
      copyError.show(error, "The query could not be copied. Select the text and copy it instead.");
    }
  };

  return (
    <SkyDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Query for ${target.name}`}
      description={
        mayRead
          ? `Run ${target.runId}. The source SQL from your repository for this run.`
          : `Run ${target.runId}.`
      }
      size="large"
      testId="transformation-sql-dialog"
      secondaryAction={{ label: "Close", onClick: onClose }}
    >
      <div className="flex flex-col gap-3">
        <InlineErrorBar
          error={copyError.error}
          title="The query could not be copied"
          testId="transformation-sql-copy-error"
        />

        {mayRead ? null : (
          <SkyMessageBar
            intent="info"
            layout="multiline"
            testId="transformation-sql-tenant-admin-only"
            message={SOURCE_SQL_TENANT_ADMIN_ONLY}
          />
        )}

        {mayRead && sql.isPending ? (
          <SkySkeleton testId="transformation-sql-loading" aria-busy="true">
            <div className="h-40 rounded-md bg-muted" />
          </SkySkeleton>
        ) : null}

        {notFound ? (
          <SkyMessageBar
            intent="info"
            layout="multiline"
            testId="transformation-sql-not-recorded"
            message={NOT_RECORDED}
          />
        ) : null}

        {sql.isError && !notFound ? (
          <SqlReadError error={sql.error} onRetry={() => void sql.refetch()} />
        ) : null}

        {data ? (
          <SqlBody
            data={data}
            shown={shown}
            name={target.name}
            copied={copied}
            messageRef={messageRef}
            onCopy={() => void copy()}
          />
        ) : null}
      </div>
    </SkyDialog>
  );
}
